import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Box, Typography, IconButton, TextField, 
  InputAdornment, CircularProgress, Alert, Divider, Tooltip
} from '@mui/material';
import { 
  GridComponent, ColumnsDirective, ColumnDirective, 
  Inject, Sort, Filter 
} from '@syncfusion/ej2-react-grids';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatIcon from '@mui/icons-material/Chat';
import NoteIcon from '@mui/icons-material/Note'; // Scegli quella che preferisci
import { cicassService } from '../../../../_services';

const drawerWidth = 400; // Larghezza ottimale per non sacrificare troppo il calendario

const AssignmentsSidePanel = ({ open, onClose, calendarId, year, month, apiUrl, t, lastUpdate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const loadData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const lang = localStorage.getItem("weblang") || "it";
      const { data: mc } = await cicassService.getAll(
        `${apiUrl}/monthlycheck/${calendarId},${year},${month},${lang}`
      );
      const enrichedData = (mc || []).map(item => {
        // Verifichiamo se monthMaxDescr esiste e non è solo spazio vuoto
        const hasDescription = item.monthMaxDescr && item.monthMaxDescr.trim() !== "";
        
        return {
            ...item,
            // Se c'è la descrizione, uniamo col trattino, altrimenti mostriamo solo il giorno
            comboTurno: hasDescription 
            ? `${item.monthProgressive} - ${item.monthMaxDescr}`
            : `${item.monthProgressive}`
        };
        });
      setData(enrichedData);
    } catch (err) {
      console.error("Errore SidePanel:", err);
    } finally {
      setLoading(false);
    }
  }, [open, calendarId, year, month, apiUrl]);

  // Ricarica i dati quando si apre il pannello O quando lastUpdate cambia (dopo un inserimento nel calendario)
  useEffect(() => {
    loadData();
  }, [loadData, lastUpdate]);

  const filteredData = data.filter(item => 
    item.nameSurname?.toLowerCase().includes(searchText.toLowerCase()) ||
    item.userNote?.toLowerCase().includes(searchText.toLowerCase()) ||
    item.comboTurno?.toLowerCase().includes(searchText.toLowerCase())
  );
  const noteTemplate = (props) => {
  if (!props.userNote || props.userNote.trim() === "" || props.userNote.trim() === "-") return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Tooltip title={props.userNote} arrow placement="left">
        <NoteIcon sx={{ fontSize: 18, color: 'action.active', cursor: 'pointer' }} />
      </Tooltip>
    </Box>
  );
};
  const monthName = new Date(year, month - 1).toLocaleString(localStorage.getItem("weblang") || 'it', { month: 'long' });
  return (
  <>
    <style>
      {`
        .e-grid .e-altrow { background-color: #f0f7ff !important; }
        .e-grid .e-row:hover { background-color: #e3f2fd !important; }
        .bold-column { font-weight: 600 !important; color: #1976d2 !important; }
      `}
    </style>
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          boxShadow: -2,
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <ChevronRightIcon />
          </IconButton>
          <Typography fontWeight="bold" sx={{ color: 'white' }}>
            {t("calendarMonthProgressive")} - {monthName} {year}
          </Typography>
        </Box>
        <Tooltip title={t("Aggiorna")}>
          <IconButton onClick={loadData} size="small" sx={{ color: 'white' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t("Cerca...")}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
            endAdornment: searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}><ClearIcon fontSize="small" /></IconButton>
            )
          }}
          sx={{ mb: 2 }}
        />

        {loading && data.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <GridComponent 
            dataSource={filteredData} 
            height="calc(100vh - 200px)" 
            gridLines="Both"
            rowHeight={40}
            enableAltRow={true}
          >
            <ColumnsDirective>
              <ColumnDirective field="nameSurname" headerText={t("Volontario")} width="160" clipMode="EllipsisWithTooltip"/>
              <ColumnDirective field="comboTurno" headerText="Turni" width="120" textAlign="Center" clipMode="EllipsisWithTooltip"/>
              <ColumnDirective field="userNote" headerText={t("Note")} width="70" textAlign="Center" template={noteTemplate} />
            </ColumnsDirective>
            <Inject services={[Sort]} />
          </GridComponent>
        )}
      </Box>
    </Drawer>
  </>
  );
};

export default AssignmentsSidePanel;